import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardHeader,
  Paper,
  Box,
  Button,
  TextField,
  Dialog, DialogTitle, DialogActions, DialogContent, DialogContentText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import IconButton from '@mui/material/IconButton';


const statusColors = {
  Sold: 'red',
  Approved: 'green',
  Pending: 'blue',
  InProgress: 'orange',
};

const statusOrder = {
  Approved: 1,
  InProgress: 2,
  Pending: 3,
  Sold: 4,
};

const ProjectPlots = ({ plots, NoOfPlots, role }) => {

  const [selectedPlot, setSelectedPlot] = useState(null);
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  // Sort by status
  const sortedPlots = [...plots].sort(
    (a, b) => statusOrder[a.Status] - statusOrder[b.Status]
  );

  // Filter plots by search term (case-insensitive)
  const filteredPlots = sortedPlots.filter((plot) => {
    const plotName = plot.PlotName || plot.PropertyName || '';
    const size = plot.Size || plot.SizeInSqFt || '';
    const status = plot.Status || '';
    return (
      plotName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(size).includes(searchTerm) ||
      status.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });
  const onCardClick = (plot) => {
    setSelectedPlot(plot);
    setModalOpen(true);
  }

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 3,
        marginTop: 4,
        maxHeight: '70vh',
        overflowY: 'auto',
        backgroundColor: '#f9f9f9',
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Project Plots
        </Typography>
        {role !== "legalteam" && (
          <Button
            variant="contained"
            disabled={NoOfPlots <= plots.length}
            onClick={() => navigate("/property/addProperty")}
          >
            Add Plot
          </Button>
        )}
      </Box>

      {/* Search Box */}
      <TextField
        label="Search plots..."
        variant="outlined"
        fullWidth
        size="small"
        sx={{ mb: 3 }}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Grid container spacing={2}>
        {filteredPlots.map((plot) => (
          <Grid item xs={12} sm={6} md={3} key={plot.id}>
            <Card
              sx={{
                height: '100%',
                borderLeft: `5px solid ${statusColors[plot.Status] || 'gray'}`,
                borderRadius: 2,
                padding: 1,
                backgroundColor: '#fff',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease-in-out',
                position: 'relative', // 🔑 REQUIRED
                ':hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                },
              }}
              onClick={role === "admin" ? () => onCardClick(plot) : undefined}
            >
              {/* 🔥 EDIT ICON */}
              {role === "admin" && (
                <IconButton
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    backgroundColor: '#fff',
                    boxShadow: 1,
                    zIndex: 2,
                    '&:hover': {
                      backgroundColor: '#f0f0f0',
                    },
                  }}
                  onClick={(e) => {
                    e.stopPropagation(); // 🔑 Prevent card click
                    navigate('/property/editProperty', {
                      state: {
                        PropertyId: plot.plotId,
                        PropertyName: plot.PlotName || plot.PropertyName,
                        SizeInSqFt: plot.Size || plot.SizeInSqFt,
                        UnitPrice: plot.SqFtPrice,
                        City: plot.City,
                        Address: plot.Address,
                        IconUrl: plot.IconUrl,
                        Status: plot.Status,

                      },
                    });
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}

              <CardHeader
                title={plot.PlotName || plot.PropertyName}
                titleTypographyProps={{
                  fontWeight: 'bold',
                  fontSize: '1rem',
                }}
                sx={{ paddingBottom: 0, paddingTop: 1 }}
              />

              <CardContent sx={{ paddingTop: 1 }}>
                <Typography variant="body2">
                  <strong>Size:</strong> {plot.Size || plot.SizeInSqFt} sq.ft
                </Typography>
                <Typography variant="body2">
                  <strong>Rate:</strong> ₹{plot.SqFtPrice} / sq.ft
                </Typography>
                <Typography variant="body2" sx={{ color: 'primary.main', mb: 1 }}>
                  <strong>Total:</strong> ₹
                  {(plot.Size * parseFloat(plot.SqFtPrice)).toLocaleString()}
                </Typography>
                {plot.percentagevalue == null ? (
                  <Box sx={{ display: "flex", marginTop: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Sqft Amount</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginLeft: 1 }}>
                      {plot.sqftamount}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", marginTop: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Percentage Value</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500, marginLeft: 1 }}>
                      {plot.percentagevalue}
                    </Typography>
                  </Box>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: statusColors[plot.Status],
                  }}
                >
                  Status: {plot.Status}
                </Typography>
              </CardContent>
            </Card>

          </Grid>
        ))}

        {/* No results found */}
        {filteredPlots.length === 0 && (
          <Grid item xs={12}>
            <Typography variant="body1" color="text.secondary">
              No plots match your search.
            </Typography>
          </Grid>
        )}
      </Grid>
      <Dialog open={modalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <DialogTitle> Manage Plots</DialogTitle>
        <DialogActions>
          {/* <Button variant='contained' onClick={()=>{
            navigate('/property/editProperty',{state:{
                  PropertyId:selectedPlot.plotId,
            PropertyName:selectedPlot.PlotName || selectedPlot.PropertyName,
            City:selectedPlot.City,
            Address:selectedPlot.Address,
      
            SizeInSqFt:selectedPlot.Size || selectedPlot.SizeInSqFt,
            UnitPrice:selectedPlot.SqFtPrice,
            IconUrl:selectedPlot.IconUrl,
            }})
          }}> Edit Plot</Button> */}
          <Button
            variant="contained"
            onClick={() => {
              if (!selectedPlot) return;
              navigate("/property/marketPrice", {
                state: {
                  PropertyId: selectedPlot.plotId,
                  PropertyName: selectedPlot.PlotName || selectedPlot.PropertyName,
                  City: selectedPlot.Size || selectedPlot.SizeInSqFt,
                  UnitPrice: selectedPlot.SqFtPrice
                }
              });
            }}
          >
            Market Update
          </Button>


        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ProjectPlots;
